import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { getDprChangeHistory } from '../core/_requests'

interface Props {
  show: boolean
  onClose: () => void
  reportId?: number
}

interface ChangeHistoryRecord {
  id: number
  reportId: number
  userId: number
  userName: string
  userRank?: string
  sectionName: string
  fieldName: string
  rowId?: number
  oldValue: string
  newValue: string
  operation: string
  createdAt: string
}

export const DprAuditHistoryDrawer: React.FC<Props> = ({ show, onClose, reportId }) => {
  const [history, setHistory] = useState<ChangeHistoryRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedSection, setSelectedSection] = useState<string>('')
  const [totalElements, setTotalElements] = useState(0)

  const fetchHistory = async () => {
    if (!reportId) return
    setLoading(true)
    try {
      const data = await getDprChangeHistory(reportId, selectedSection || undefined, 0, 100)
      if (data && data.content) {
        setHistory(data.content)
        setTotalElements(data.totalElements || data.content.length)
      } else if (Array.isArray(data)) {
        setHistory(data)
        setTotalElements(data.length)
      }
    } catch (err) {
      console.error('Failed to load DPR history:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (show && reportId) {
      fetchHistory()
    }
  }, [show, reportId, selectedSection])

  const formatTimestamp = (iso: string) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  return (
    <Modal show={show} onHide={onClose} size='lg' centered scrollable>
      <Modal.Header closeButton className='border-bottom py-3'>
        <div className='d-flex align-items-center gap-2'>
          <i className='bi bi-shield-check text-primary fs-3'></i>
          <div>
            <Modal.Title className='fs-5 fw-bold'>
              SIRE 2.0 / ISM Position Report Change History
            </Modal.Title>
            <span className='text-muted fs-8'>
              Audit log of all auto-saved modifications for Report #{reportId}
            </span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className='p-4 bg-light'>
        {/* ── Filters ── */}
        <div className='d-flex align-items-center justify-content-between mb-4 bg-white p-3 rounded border'>
          <div className='d-flex align-items-center gap-2'>
            <label className='fs-7 fw-semibold text-muted mb-0'>Filter Section:</label>
            <select
              className='form-select form-select-sm form-select-solid w-200px'
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
            >
              <option value=''>All Sections</option>
              <option value='HEADER'>Header & Basic Info</option>
              <option value='NAVIGATION'>Navigation & Weather</option>
              <option value='ACTIVITY_SUMMARY'>Activity Summary</option>
              <option value='TIDES'>Tides & Sun</option>
              <option value='FO_CONSUMPTION'>FO Consumption</option>
              <option value='MACHINERY_FO'>Machinery FO</option>
              <option value='ACTIVITIES'>Activities Log</option>
              <option value='ROB'>ROB Status</option>
              <option value='LUBE_OIL'>Lube Oil Status</option>
              <option value='WEATHER_ROWS'>Weather (4-hourly)</option>
              <option value='DOWNTIME'>Downtime</option>
              <option value='POB'>POB</option>
              <option value='SAFETY'>Safety</option>
            </select>
          </div>

          <button
            type='button'
            className='btn btn-sm btn-icon btn-light-primary'
            onClick={fetchHistory}
            title='Refresh'
          >
            <i className='bi bi-arrow-clockwise'></i>
          </button>
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div className='text-center py-5'>
            <div className='spinner-border text-primary' role='status' />
            <div className='text-muted fs-7 mt-2'>Loading change trail...</div>
          </div>
        ) : history.length === 0 ? (
          <div className='text-center py-5 bg-white rounded border'>
            <i className='bi bi-journal-check text-muted fs-1 mb-2 d-block'></i>
            <span className='fw-bold text-gray-600'>No modification history recorded yet</span>
            <div className='text-muted fs-8 mt-1'>
              Auto-saved changes will be timestamped and logged here automatically.
            </div>
          </div>
        ) : (
          <div className='timeline timeline-5'>
            {history.map((record) => (
              <div key={record.id} className='timeline-item mb-3 bg-white p-3 rounded border'>
                <div className='d-flex align-items-center justify-content-between mb-1'>
                  <div className='d-flex align-items-center gap-2'>
                    <span className='badge badge-light-primary fw-bold text-uppercase fs-9'>
                      {record.sectionName}
                    </span>
                    <span className='fw-bolder text-dark fs-7'>{record.fieldName}</span>
                  </div>
                  <span className='text-muted fs-8'>
                    <i className='bi bi-clock me-1'></i>
                    {formatTimestamp(record.createdAt)}
                  </span>
                </div>

                <div className='d-flex align-items-center gap-3 mt-2 bg-light p-2 rounded'>
                  <div className='flex-grow-1 text-truncate'>
                    <span className='text-muted fs-8 d-block'>Previous:</span>
                    <span className='text-danger fs-8 text-decoration-line-through'>
                      {record.oldValue || '—'}
                    </span>
                  </div>
                  <i className='bi bi-arrow-right text-muted fs-7'></i>
                  <div className='flex-grow-1 text-truncate'>
                    <span className='text-muted fs-8 d-block'>New:</span>
                    <span className='text-success fw-bold fs-8'>{record.newValue || '—'}</span>
                  </div>
                </div>

                <div className='d-flex align-items-center justify-content-between mt-2 pt-1 border-top border-light'>
                  <div className='d-flex align-items-center gap-1 fs-8 text-muted'>
                    <i className='bi bi-person-circle'></i>
                    <span className='fw-semibold text-gray-800'>{record.userName}</span>
                    {record.userRank && (
                      <span className='badge badge-secondary py-0 px-1 fs-9'>
                        {record.userRank}
                      </span>
                    )}
                  </div>
                  <span className='badge badge-light-secondary fs-9'>{record.operation}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className='py-2'>
        <span className='text-muted fs-8 me-auto'>
          Showing {history.length} of {totalElements} changes
        </span>
        <button type='button' className='btn btn-sm btn-light' onClick={onClose}>
          Close
        </button>
      </Modal.Footer>
    </Modal>
  )
}
